import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiImageUrl } from '../utils/streamApi';
import EvidenceStrip from './EvidenceStrip';

/**
 * Full-screen labelling view for BOTH verdict logs — probable-missed entries and
 * fired alerts. Both matter: the missed log picks a threshold, but only the alert
 * log can measure what that threshold COSTS, because a new false alarm shows up as
 * a fired alert, never as a missed entry.
 *
 * Labelling used to mean squinting at a card grid and guessing why the gate said no.
 * The ev dict already carries every value the gate compared, so this renders the
 * decision cascade in the order _rule_gate() actually evaluates it, marks the row
 * that decided, and shows the failing measurement against the bar it missed.
 *
 * Labelling auto-advances, because these arrive in the hundreds and a verdict that
 * costs two clicks and a scroll does not get done.
 */

/* Mirrors the cascade in industrial_detectors._rule_gate(), in evaluation order.
 * `applies` decides whether a row could fire at all (skip vs evaluated); `conds`
 * are the conjunction members, each reporting its own actual-vs-required. */
function buildTrace(ev = {}) {
  const n = (v, d = 0) => (v === null || v === undefined ? d : Number(v));
  const faceLarge   = !!ev.ev_face_large;
  const faceSmall   = !!ev.ev_face_small;
  const skeleton    = !!ev.ev_skeleton;
  const clothing    = !!ev.ev_clothing;
  const clipHuman   = !!ev.ev_clip_human;
  const clipFaceLg  = !!ev.ev_clip_human_face_large;
  const nonOsc      = !!ev.ev_non_osc_track;
  const pathPx      = n(ev.ev_path_px);
  const netRatio    = n(ev.ev_net_ratio);
  const yoloBest    = n(ev.ev_yolo_conf_best);
  const nightMin    = ev.ev_night_conf_min == null ? null : n(ev.ev_night_conf_min);
  const kpCount     = n(ev.ev_kp_conf_count);
  const kpMean      = n(ev.ev_kp_conf_mean);
  const segScore    = n(ev.ev_segment_score);

  // The gate compares path against small_face_min_path_px and net_ratio against the
  // scene-mode floor. Neither bar is recorded on the entry, so show the shipped
  // defaults and label them as such rather than inventing precision.
  const PATH_BAR  = 50;
  const RATIO_BAR = 0.25;
  const c = (label, ok, detail) => ({ label, ok, detail });

  return [
    {
      row: 1, name: 'FACE_LARGE', applies: faceLarge,
      skip: 'no large face',
      conds: [
        c('large face', faceLarge, faceLarge ? `${n(ev.ev_face_w)}×${n(ev.ev_face_h)}px` : 'none'),
        c('clothing', clothing),
        c('CLIP (face-large bar)', clipFaceLg, `${n(ev.ev_clip_score).toFixed(3)}`),
      ],
    },
    {
      row: 2, name: 'FACE_SMALL', applies: faceSmall && skeleton,
      skip: faceSmall ? 'no skeleton' : 'no small face',
      conds: [
        c('small face', faceSmall),
        c('skeleton (elbow+knee)', skeleton),
        c('path', pathPx >= PATH_BAR, `${pathPx.toFixed(0)}px ${pathPx >= PATH_BAR ? '≥' : '<'} ${PATH_BAR}px`),
        c('directional', netRatio >= RATIO_BAR, `net ratio ${netRatio.toFixed(2)} ${netRatio >= RATIO_BAR ? '≥' : '<'} ${RATIO_BAR}`),
        c('clothing', clothing),
        c('CLIP', clipHuman, `${n(ev.ev_clip_score).toFixed(3)}`),
      ],
    },
    {
      row: 3, name: 'SKELETON', applies: skeleton,
      skip: 'no skeleton — pose never confirmed elbow+knee',
      conds: [
        c('skeleton (elbow+knee)', skeleton, `${kpCount} kp ≥ bar, mean ${kpMean.toFixed(2)}`),
        c('path', pathPx >= PATH_BAR, `${pathPx.toFixed(0)}px ${pathPx >= PATH_BAR ? '≥' : '<'} ${PATH_BAR}px`),
        c('not oscillating', nonOsc),
        c('directional', netRatio >= RATIO_BAR, `net ratio ${netRatio.toFixed(2)} ${netRatio >= RATIO_BAR ? '≥' : '<'} ${RATIO_BAR}`),
        c('clothing', clothing),
        c('CLIP', clipHuman, `${n(ev.ev_clip_score).toFixed(3)}`),
      ],
    },
    {
      row: 4, name: 'POSE_OVERRIDE', applies: skeleton && pathPx > 0,
      skip: 'needs skeleton + any movement',
      conds: [
        c('keypoint count', kpCount >= 10, `${kpCount} ${kpCount >= 10 ? '≥' : '<'} 10`),
        c('keypoint mean', kpMean >= 0.50, `${kpMean.toFixed(2)} ${kpMean >= 0.50 ? '≥' : '<'} 0.50`),
        c('segment score', segScore >= 0.60, `${segScore.toFixed(2)} ${segScore >= 0.60 ? '≥' : '<'} 0.60`),
        c('not oscillating', nonOsc),
        c('directional', netRatio >= RATIO_BAR, `net ratio ${netRatio.toFixed(2)}`),
      ],
    },
    {
      row: 5, name: 'NIGHT_CONF', applies: nightMin != null && nightMin > 0,
      skip: 'night confidence door is off for this camera',
      conds: [
        c('person confidence', nightMin != null && yoloBest >= nightMin,
          `${yoloBest.toFixed(3)} ${nightMin != null && yoloBest >= nightMin ? '≥' : '<'} ${nightMin != null ? nightMin.toFixed(2) : '—'}`),
        c('clothing', clothing),
        c('CLIP', clipHuman),
        c('not oscillating', nonOsc),
      ],
    },
  ];
}

function Cond({ c }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, padding: '1px 0' }}>
      <span style={{ width: 14, flex: '0 0 14px', color: c.ok ? '#22c55e' : '#ef4444' }}>{c.ok ? '✓' : '✗'}</span>
      <span style={{ flex: 1, color: c.ok ? 'var(--text-muted)' : 'var(--text)' }}>{c.label}</span>
      {c.detail && (
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: c.ok ? 'var(--text-muted)' : '#ef4444' }}>
          {c.detail}
        </span>
      )}
    </div>
  );
}

function RuleTrace({ ev }) {
  const rows = useMemo(() => buildTrace(ev), [ev]);
  const passed = String(ev?.alert_rule || '').startsWith('RULE_PASS');
  // The deciding row is the first that applied and did not pass — the same
  // short-circuit the cascade itself performs.
  const decidedIdx = rows.findIndex(r => r.applies && r.conds.some(c => !c.ok));

  return (
    <div>
      {rows.map((r, i) => {
        const isDecider = i === decidedIdx && !passed;
        const failed    = r.applies && r.conds.some(c => !c.ok);
        return (
          <div key={r.row} style={{
            marginBottom: 6, padding: isDecider ? '6px 8px' : '3px 8px',
            borderLeft: `3px solid ${isDecider ? '#ef4444' : r.applies ? 'var(--border)' : 'transparent'}`,
            background: isDecider ? 'rgba(239,68,68,0.06)' : 'transparent',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{r.row}</span>
              <strong style={{ fontSize: 12, color: r.applies ? 'var(--text)' : 'var(--text-muted)' }}>{r.name}</strong>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: !r.applies ? 'var(--text-muted)' : failed ? '#ef4444' : '#22c55e' }}>
                {!r.applies ? 'skip' : failed ? 'FAIL' : 'pass'}
              </span>
            </div>
            {!r.applies && <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 22 }}>{r.skip}</div>}
            {r.applies && (isDecider || !failed) && (
              <div style={{ paddingLeft: 14, marginTop: 2 }}>
                {r.conds.map((c, j) => <Cond key={j} c={c} />)}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 12 }}>
        <div style={{ fontFamily: 'monospace', color: passed ? '#22c55e' : '#ef4444' }}>
          → {ev?.alert_rule || 'unknown'}
        </div>
        {ev?.ev_rule_block_reason && (
          <div style={{ marginTop: 3, color: 'var(--text-muted)' }}>
            Failed on: <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{ev.ev_rule_block_reason}</span>
          </div>
        )}
        <div style={{ marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-muted)' }}>
          <span>person conf <strong style={{ color: 'var(--text)' }}>{Number(ev?.ev_yolo_conf_best ?? 0).toFixed(3)}</strong></span>
          {ev?.ev_yolo_frame_count != null && (
            <span>frames <strong style={{ color: 'var(--text)' }}>{ev.ev_yolo_frame_count}</strong></span>
          )}
          <span>path <strong style={{ color: 'var(--text)' }}>{Number(ev?.ev_path_px ?? 0).toFixed(0)}px</strong></span>
        </div>
        {ev?.ev_yolo_conf_trace && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5,
                          color: 'var(--text-muted)', marginBottom: 3 }}>
              Confidence per frame
            </div>
            <ConfSparkline
              trace={String(ev.ev_yolo_conf_trace).split(',').map(Number).filter(v => !isNaN(v))}
              bar={ev.ev_night_conf_min == null ? null : Number(ev.ev_night_conf_min)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* Whether a session sat just under the bar for many frames (lower the number) or
 * spiked over it once (needs persistence instead) is invisible in a single "best"
 * value, and the two want opposite fixes. */
function ConfSparkline({ trace, bar }) {
  if (!trace.length) return null;
  const W = 320, H = 40, n = trace.length;
  const x = i => (n === 1 ? W / 2 : (i / (n - 1)) * W);
  const y = v => H - Math.min(1, Math.max(0, v)) * H;
  const pts = trace.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 40, display: 'block' }}
         preserveAspectRatio="none" role="img" aria-label="person confidence per frame">
      <rect x="0" y="0" width={W} height={H} fill="var(--surface-alt, rgba(127,127,127,0.08))" />
      {bar > 0 && (
        <line x1="0" x2={W} y1={y(bar)} y2={y(bar)} stroke="#22c55e"
              strokeWidth="1" strokeDasharray="4 3" />
      )}
      <polyline points={pts} fill="none" stroke="var(--primary, #3b82f6)" strokeWidth="1.5" />
      {trace.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="1.8"
                fill={bar > 0 && v >= bar ? '#22c55e' : '#ef4444'} />
      ))}
    </svg>
  );
}

// Default shape = probable_missed entries. The alerts log keeps its ev dict under
// `telemetry` and is keyed by filename, so both are passed in as accessors rather
// than forking the component.
const MISSED_VERDICTS = [
  { verdict: 'confirmed_missed', label: '✗ Missed', key: 'm', colour: '#ef4444' },
  { verdict: 'correct_block',    label: '✓ Block',  key: 'c', colour: null },
];

// A machine suggestion, shown immediately above the verdict buttons.
//
// It answers ONE question — is a human visible in this picture — and deliberately
// not "was blocking correct", which is the distinction the whole labelling effort
// turns on. `correct_block` records a correct decision not to alert: a guard
// seated at his post is a human the system was right to ignore. Treating the two
// as the same thing contaminated 5 of 21 negatives at one site and 6 of 10 at
// another, and fixing only those labels moved a CLIP gate from AUC 0.81 to 0.965.
//
// Placed here on purpose. A reviewer about to press "✓ Block" on a record where
// this says a person is present is at the exact moment that distinction matters,
// and a line of text in front of them does what no amount of documentation has.
//
// It never changes the verdict and never preselects a button. The human label is
// the only authoritative one, which is also what keeps agreement between the two
// measurable over time.
function AiSuggestion({ ai }) {
  if (!ai || !ai.human_present) return null;
  const yes = ai.human_present === 'yes';
  const unsure = ai.human_present === 'uncertain';
  const colour = unsure ? '#f59e0b' : (yes ? '#ef4444' : 'var(--text-muted)');
  return (
    <div style={{
      margin: '0 0 8px', padding: '7px 9px', borderRadius: 6, fontSize: 12,
      border: `1px solid ${colour}55`, color: 'var(--text)',
      background: 'var(--surface-2, rgba(255,255,255,0.03))',
    }}>
      <strong style={{ color: colour }}>
        {unsure ? 'Not sure' : yes ? 'Looks like a person' : 'Looks like an object'}
      </strong>
      {ai.confidence ? <span style={{ color: 'var(--text-muted)' }}> · {ai.confidence} confidence</span> : null}
      {!yes && ai.what_it_is ? <span> — {ai.what_it_is}</span> : null}
      {yes ? (
        <div style={{ color: 'var(--text-muted)', marginTop: 3 }}>
          A correct block can still contain a real person. Judge the decision, not the picture.
        </div>
      ) : null}
    </div>
  );
}

// The face is the fastest way to answer "was this a person at all?" — the question
// a blocked record is labelled on. Hidden entirely when there is none, so the layout
// is unchanged for the many visits that never capture a face.
function ReviewFace({ path }) {
  const [src, setSrc] = useState(null);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    let live = true;
    setSrc(null); setGone(false);
    apiImageUrl(path).then(s => { if (live) setSrc(s); });
    return () => { live = false; };
  }, [path]);
  if (!src || gone) return null;
  return (
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
      <img src={src} alt="face"
           onError={() => setGone(true)}
           style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6,
                    border: '1px solid var(--border)' }} />
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Face captured this visit</span>
    </div>
  );
}

export default function ReviewView({
  entries, startIndex = 0, onClose, onVerdict, fmtTs,
  getEv      = e => e.ev || {},
  getId      = e => e.entry_id,
  getCam     = e => e.cam_name,
  getTs      = e => e.ts,
  getSubtitle = e => e.block_path,
  subtitleLabel = 'block path',
  imageUrlFor = e => `/admin/missed-alerts/${e.entry_id}/snapshot`,
  // Context strip under the main image. Defaults to the blocked-record routes;
  // AlertsPage overrides both with the alert sidecar routes. Returning null from
  // either disables the strip for a caller that has none.
  stripCountFor = e => `/admin/missed-alerts/${e.entry_id}/frames`,
  stripFrameFor = e => i => `/admin/missed-alerts/${e.entry_id}/frame/${i}`,
  // Face crop, when the visit captured one. 404s are normal — at night most visits
  // never produce a face — so the thumbnail simply hides itself.
  faceUrlFor    = e => `/admin/missed-alerts/${e.entry_id}/face`,
  verdicts   = MISSED_VERDICTS,
  reasons    = null,          // when set, the first verdict opens a reason picker.
                              // May be a function (entry) => list — face-cover alerts
                              // have their own reasons; an array behaves as before.
  reasonFor  = null,
}) {
  const [idx, setIdx]   = useState(startIndex);
  const [src, setSrc]   = useState(null);
  const [busy, setBusy] = useState(false);

  const [pendingReason, setPendingReason] = useState(null);
  const entry   = entries[idx];
  const reasonList = typeof reasons === 'function' ? (entry ? reasons(entry) : null) : reasons;
  const total   = entries.length;
  const entryId = entry ? getId(entry) : null;
  const imgPath = entry ? imageUrlFor(entry) : null;

  // Keyed on the id alone: the entry object is replaced on every verdict save, and
  // depending on it would refetch the same image each time a label is applied.
  useEffect(() => {
    if (!entryId) return;
    let live = true;
    setSrc(null);
    apiImageUrl(imgPath).then(s => { if (live) setSrc(s); });
    return () => { live = false; };
  }, [entryId, imgPath]);

  const go = useCallback(d => setIdx(i => Math.min(total - 1, Math.max(0, i + d))), [total]);

  // Auto-advance: the point of this view is getting through a night's worth of
  // entries. Stops at the last one rather than wrapping, so it is obvious when done.
  const label = useCallback(async (verdict, reason = null) => {
    if (!entry || busy) return;
    setBusy(true);
    try { await onVerdict(getId(entry), verdict, reason); } finally { setBusy(false); }
    setPendingReason(null);
    setIdx(i => Math.min(total - 1, i + 1));
  }, [entry, busy, onVerdict, total, getId]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape')      { onClose(); return; }
      if (e.key === 'ArrowLeft')   { go(-1); return; }
      if (e.key === 'ArrowRight')  { go(1);  return; }
      const k = e.key.toLowerCase();
      const hit = verdicts.find(v => v.key === k);
      // A verdict that needs a reason opens the picker instead of saving blind.
      if (hit) { if (reasonList && hit.needsReason) setPendingReason(hit.verdict); else label(hit.verdict); }
      if (k === 'u') label(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, label, onClose, verdicts, reasonList]);

  if (!entry) return null;
  const unlabelled = entries.filter(e => !e.verdict).length;

  const btn = (text, verdict, colour, needsReason) => (
    <button
      key={verdict}
      className={`alert-action-btn${entry.verdict === verdict ? ' active' : ''}`}
      disabled={busy}
      onClick={() => (reasonList && needsReason ? setPendingReason(verdict) : label(verdict))}
      style={{ flex: 1, padding: '10px 8px', fontSize: 13,
               ...(colour ? { color: colour, borderColor: `${colour}66` } : {}) }}
    >{text}</button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000, background: 'var(--bg, #0b0b0d)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
        borderBottom: '1px solid var(--border)', flex: '0 0 auto',
      }}>
        <strong style={{ fontSize: 14 }}>{getCam(entry)}</strong>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{fmtTs ? fmtTs(getTs(entry)) : ''}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>
          {idx + 1} / {total}{unlabelled ? ` · ${unlabelled} unlabelled` : ' · all labelled'}
        </span>
        {/* NOT .dash-sheet-close — that class is width:100% for bottom sheets, which
            stretches the button across the header and leaves the glyph centred. */}
        <button
          onClick={onClose}
          aria-label="Close"
          title="Close (Esc)"
          style={{
            flex: '0 0 auto', width: 32, height: 32, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, fontSize: 16, cursor: 'pointer',
            background: 'transparent', color: 'var(--text-muted)',
            border: '1px solid var(--border)', borderRadius: 8,
          }}
        >✕</button>
      </div>

      {/* Body: frame left, labelling + trace right */}
      <div className="missed-fullview-body" style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{
          flex: '1 1 60%', minWidth: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#000', position: 'relative',
        }}>
          {src
            ? <img src={src} alt="frame" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <span style={{ color: 'var(--text-muted)' }}>📷 loading…</span>}
          <button onClick={() => go(-1)} disabled={idx === 0}
            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                     opacity: idx === 0 ? 0.25 : 0.8, fontSize: 22, padding: '6px 12px',
                     background: 'rgba(0,0,0,0.5)', color: '#fff', border: 0, borderRadius: 6,
                     cursor: idx === 0 ? 'default' : 'pointer' }}>‹</button>
          <button onClick={() => go(1)} disabled={idx === total - 1}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                     opacity: idx === total - 1 ? 0.25 : 0.8, fontSize: 22, padding: '6px 12px',
                     background: 'rgba(0,0,0,0.5)', color: '#fff', border: 0, borderRadius: 6,
                     cursor: idx === total - 1 ? 'default' : 'pointer' }}>›</button>
        </div>

        {entry && faceUrlFor && <ReviewFace path={faceUrlFor(entry)} />}

        {entry && stripCountFor && stripFrameFor && (
          <EvidenceStrip
            countPath={stripCountFor(entry)}
            framePath={stripFrameFor(entry)}
            label="Context frames"
          />
        )}

        <div style={{
          flex: '0 0 360px', maxWidth: '100%', overflowY: 'auto', padding: 14,
          borderLeft: '1px solid var(--border)',
        }}>
          <AiSuggestion ai={entry.ai_verdict} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            {verdicts.map(v => btn(v.label, v.verdict, v.colour, v.needsReason))}
          </div>
          {pendingReason && reasonList && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 10px' }}>
              {reasonList.map(r => (
                <button key={r.value} className="alert-action-btn" disabled={busy}
                  style={{ fontSize: 11, padding: '5px 8px' }}
                  onClick={() => label(pendingReason, r.value)}>{r.label}</button>
              ))}
              <button className="alert-action-btn" style={{ fontSize: 11, padding: '5px 8px' }}
                onClick={() => setPendingReason(null)}>cancel</button>
            </div>
          )}
          <button className="alert-action-btn" disabled={busy} onClick={() => label(null)}
            style={{ width: '100%', marginBottom: 12, fontSize: 12 }}>Unsure — skip</button>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 14px' }}>
            {verdicts.map(v => <span key={v.verdict}><kbd>{v.key.toUpperCase()}</kbd> {v.label.replace(/^[^ ]+ /, '')} · </span>)}
            <kbd>U</kbd> unsure · <kbd>←</kbd><kbd>→</kbd> move · <kbd>Esc</kbd> close
          </p>

          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6,
                        color: 'var(--text-muted)', marginBottom: 6 }}>Rule trace</div>
          <RuleTrace ev={getEv(entry)} />

          {getSubtitle(entry) != null && (
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
              {subtitleLabel}: <span style={{ fontFamily: 'monospace' }}>{getSubtitle(entry)}</span>
              {reasonFor && reasonFor(entry) ? ` · ${reasonFor(entry)}` : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
